import type { PointerEvent as ReactPointerEvent } from 'react';
import type { ChartDrawingTool } from '../types';
import { DRAWING_TOOL_ORDER, getDrawingToolLabel } from '../lib/chartDrawings';

interface Props {
  activeTool: ChartDrawingTool;
  onSelectTool: (tool: ChartDrawingTool) => void;
  hidden?: boolean;
  orientation?: 'vertical' | 'horizontal';
  showDragHandle?: boolean;
  collapsed?: boolean;
  onDragHandlePointerDown?: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  className?: string;
}

function iconForTool(tool: ChartDrawingTool) {
  switch (tool) {
    case 'cursor':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M5 4l9 9-4 1 2 5-2 1-2-5-3 3V4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case 'horizontalLine':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M3 12h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'horizontalRay':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <circle cx="5" cy="12" r="1.5" fill="currentColor" />
          <path d="M7.5 12H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'trendLine':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M5 17L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="5" cy="17" r="1.5" fill="currentColor" />
          <circle cx="19" cy="7" r="1.5" fill="currentColor" />
        </svg>
      );
    case 'ray':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <circle cx="5" cy="17" r="1.5" fill="currentColor" />
          <path d="M6.5 16L21 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'extendedLine':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M3 18L21 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'rectangle':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <rect x="5" y="7" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case 'parallelChannel':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M4 16L18 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M6 20L20 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'fibRetracement':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M6 5v14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M8 7h10M8 11h8M8 15h6M8 19h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'text':
      return (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M6 6h12M12 6v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
  }
}

function dragHandleIcon(collapsed: boolean) {
  if (collapsed) {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
        <path d="M6 18l3.4-.8L18 8.6a1.4 1.4 0 000-2L17.4 6a1.4 1.4 0 00-2 0l-8.6 8.6L6 18z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M14.5 7.5l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <path d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

export function ChartDrawingToolbar({
  activeTool,
  onSelectTool,
  hidden = false,
  orientation = 'vertical',
  showDragHandle = false,
  collapsed = false,
  onDragHandlePointerDown,
  className = '',
}: Props) {
  const handleLabel = collapsed ? 'Show drawing tools' : 'Move drawing tools';
  const handleContent = dragHandleIcon(collapsed);
  const toolbarLayoutClass =
    orientation === 'vertical'
      ? 'flex-col items-stretch rounded-[18px]'
      : 'flex flex-row items-center rounded-2xl';
  const handleClass =
    orientation === 'vertical'
      ? 'inline-flex h-8 w-9 shrink-0 touch-none items-center justify-center rounded-xl border border-slate-700/80 bg-slate-900/90 text-slate-300 transition-colors hover:bg-slate-800 active:cursor-grabbing'
      : 'inline-flex h-9 w-8 shrink-0 touch-none items-center justify-center rounded-xl border border-slate-700/80 bg-slate-900/90 text-slate-300 transition-colors hover:bg-slate-800 active:cursor-grabbing';
  const passiveHandleClass =
    orientation === 'vertical'
      ? 'inline-flex h-8 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-700/80 bg-slate-900/90 text-slate-300'
      : 'inline-flex h-9 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-700/80 bg-slate-900/90 text-slate-300';

  return (
    <div
      data-chart-context-ignore="true"
      className={`${toolbarLayoutClass} z-[65] flex gap-1 border border-slate-700/80 bg-slate-950/96 p-1.5 shadow-[0_14px_30px_rgba(2,8,24,0.55)] backdrop-blur ${hidden ? 'opacity-0 pointer-events-none' : ''} ${className}`}
    >
      {showDragHandle ? (
        onDragHandlePointerDown ? (
          <button
            type="button"
            aria-label={handleLabel}
            title={handleLabel}
            onPointerDown={onDragHandlePointerDown}
            className={handleClass}
          >
            {handleContent}
          </button>
        ) : (
          <div
            aria-hidden="true"
            className={passiveHandleClass}
          >
            {handleContent}
          </div>
        )
      ) : null}
      {!collapsed ? DRAWING_TOOL_ORDER.map((tool) => {
        const isActive = tool === activeTool;
        return (
          <button
            key={tool}
            type="button"
            title={getDrawingToolLabel(tool)}
            aria-label={getDrawingToolLabel(tool)}
            aria-pressed={isActive}
            onClick={() => onSelectTool(tool)}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border text-slate-100 transition-colors ${
              isActive
                ? 'border-sky-400/70 bg-sky-500/18 text-sky-200 shadow-[0_8px_18px_rgba(14,165,233,0.18)]'
                : 'border-transparent bg-transparent hover:border-slate-700 hover:bg-slate-900/90'
            }`}
          >
            {iconForTool(tool)}
          </button>
        );
      }) : null}
    </div>
  );
}
